import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link2, Link2Off, Loader2, CheckCircle2, Sparkles } from 'lucide-react';
import toast from 'react-hot-toast';
import { listPublivoOrgs, connectPublivo, type PublivoOrg } from '@/lib/analytics-api';

interface Props {
  projectId: string;
  projectName: string;
  connectedOrgId: string | null;
  /** gsc_property des Projekts, z.B. "sc-domain:nico-els.de" – Basis fürs Auto-Match. */
  projectGsc?: string | null;
}

/** Hostname aus URL/Host normalisieren (ohne Protokoll, www, Slash). */
function hostOf(v?: string | null): string | null {
  if (!v) return null;
  let s = v.trim().toLowerCase();
  if (!s) return null;
  try {
    if (s.includes('://')) s = new URL(s).hostname;
  } catch {
    /* ignore */
  }
  return s.replace(/^www\./, '').replace(/\/+$/, '') || null;
}

/** Projekt-Domain aus gsc_property ableiten (sc-domain: oder URL-Präfix). */
function domainFromGsc(gsc?: string | null): string | null {
  if (!gsc) return null;
  const s = gsc.trim();
  if (s.toLowerCase().startsWith('sc-domain:')) return hostOf(s.slice('sc-domain:'.length));
  return hostOf(s);
}

/** Gleiche Site? Gleich oder Sub-/Überdomain (nico-els.de ↔ blog.nico-els.de). */
function sameSite(a?: string | null, b?: string | null): boolean {
  const x = hostOf(a);
  const y = hostOf(b);
  if (!x || !y) return false;
  return x === y || x.endsWith('.' + y) || y.endsWith('.' + x);
}

/** Admin-UI: ein Projekt mit einer PUBLIVO-Org verbinden/trennen (mit Auto-Match). */
export default function PublivoConnector({ projectId, projectName, connectedOrgId, projectGsc }: Props) {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<string>(connectedOrgId || '');

  const { data, isLoading } = useQuery({
    queryKey: ['publivo-orgs'],
    queryFn: listPublivoOrgs,
    staleTime: 300_000,
  });

  const projectDomain = useMemo(() => domainFromGsc(projectGsc), [projectGsc]);

  // Auto-Match: passende Org anhand der Domain (Blog-Domain oder Website-URL)
  const suggested: PublivoOrg | null = useMemo(() => {
    if (!data?.orgs || !projectDomain) return null;
    const matches = data.orgs.filter(
      (o) => sameSite(projectDomain, o.blog_domain) || sameSite(projectDomain, o.website_url)
    );
    if (matches.length === 0) return null;
    // Live-Blog bevorzugen
    return matches.find((m) => m.has_live_blog) || matches[0];
  }, [data?.orgs, projectDomain]);

  // Vorschlag vorauswählen, solange der Admin noch nichts manuell gewählt hat
  useEffect(() => {
    if (!connectedOrgId && suggested && !selected) {
      setSelected(suggested.id);
    }
  }, [suggested, connectedOrgId, selected]);

  const mutation = useMutation({
    mutationFn: (orgId: string | null) => connectPublivo(projectId, orgId),
    onSuccess: (_res, orgId) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] });
      queryClient.invalidateQueries({ queryKey: ['blog'] });
      toast.success(orgId ? 'PUBLIVO verbunden' : 'Verbindung getrennt');
    },
    onError: (err: any) => toast.error(err.message || 'Aktion fehlgeschlagen'),
  });

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 flex justify-center">
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data?.configured) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6 text-sm text-gray-500">
        Die PUBLIVO-Verbindung ist serverseitig noch nicht konfiguriert (Env-Variablen
        <code className="mx-1 px-1 bg-gray-100 rounded">PUBLIVO_SUPABASE_URL</code> /
        <code className="mx-1 px-1 bg-gray-100 rounded">PUBLIVO_SERVICE_ROLE_KEY</code>).
      </div>
    );
  }

  const orgs = data.orgs;
  const connectedOrg = orgs.find((o) => o.id === connectedOrgId);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-1">
        <Link2 className="w-4 h-4 text-primary-600" />
        <h2 className="text-sm font-semibold text-gray-900">PUBLIVO-Blog verbinden</h2>
      </div>
      <p className="text-xs text-gray-500 mb-4">
        Verknüpfe „{projectName}" mit dem passenden PUBLIVO-Blog, um Artikel-Leistung und
        Themen-Vorschläge zu sehen. Kunden ohne PUBLIVO lässt du einfach unverbunden.
      </p>

      {connectedOrg ? (
        <div className="flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
          <div className="flex items-center gap-2 text-sm">
            <CheckCircle2 className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-900">Verbunden mit {connectedOrg.name}</span>
            {connectedOrg.blog_domain && <span className="text-green-700">· {connectedOrg.blog_domain}</span>}
          </div>
          <button
            onClick={() => mutation.mutate(null)}
            disabled={mutation.isPending}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 text-red-600 text-xs font-medium hover:bg-red-50"
          >
            {mutation.isPending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2Off className="w-3.5 h-3.5" />}
            Trennen
          </button>
        </div>
      ) : (
        <>
          {/* Auto-Match-Vorschlag */}
          {suggested && (
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3 p-3 rounded-lg bg-violet-50 border border-violet-200">
              <div className="flex items-start gap-2 text-sm">
                <Sparkles className="w-4 h-4 text-violet-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="font-medium text-violet-900">Automatisch erkannt: {suggested.name}</p>
                  <p className="text-xs text-violet-700">
                    Passende Domain{suggested.blog_domain ? ` · ${suggested.blog_domain}` : ''}
                    {!suggested.has_live_blog && ' · (Blog noch nicht live)'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => mutation.mutate(suggested.id)}
                disabled={mutation.isPending}
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
                Automatisch verbinden
              </button>
            </div>
          )}

          {/* Manuelle Auswahl (Vorschlag ist vorausgewählt) */}
          <div className="flex flex-wrap items-center gap-2">
            <select
              value={selected}
              onChange={(e) => setSelected(e.target.value)}
              className="flex-1 min-w-[220px] px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
            >
              <option value="">{suggested ? 'Andere Org wählen…' : 'PUBLIVO-Kunde auswählen…'}</option>
              {orgs.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name}
                  {o.blog_domain ? ` — ${o.blog_domain}` : o.has_live_blog ? ' — Blog live' : ' — kein Live-Blog'}
                </option>
              ))}
            </select>
            <button
              onClick={() => selected && mutation.mutate(selected)}
              disabled={!selected || mutation.isPending}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium disabled:opacity-50"
            >
              {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
              Verbinden
            </button>
          </div>
        </>
      )}
    </div>
  );
}
